import { useEffect, useRef } from "react";
import styles from "./ScanScene.module.css";

/*
 * A simulated LiDAR corridor with a stopping-sight-distance check running along it.
 * Decorative (aria-hidden) but not arbitrary: sight lines use AASHTO's driver eye height,
 * object height and the Green Book's required SSD at 55 mph, and the "blocked by" labels
 * are classes the real SSD analysis reports. World units are metres; z runs along the
 * road, x across it, y up. The road is periodic in z, so the scene loops without seams.
 */

const LOOP = 1200;
const FT = 3.28084;
const EYE_H = 1.08; // 3.5 ft
const OBJ_H = 0.6; // 2.0 ft
const REQUIRED_FT = 495; // AASHTO Green Book (2018) Table 3-1, 55 mph
const REQUIRED_M = REQUIRED_FT / FT;
const LOOK_STEP = 6;
const MAX_LOOK = 258;
const STATION_M = 4;
const LANE_OFF = 1.8; // centre of the right lane
const SPEED = 17; // m/s of scene travel
const VEHICLE_AHEAD = 82; // analysis front, metres ahead of the camera
const STATION_ORIGIN_FT = 12000;

// Story mode (scroll-driven, `progressRef`): where the analysis front travels as the page
// scrolls. The defaults are the home page's three-step story, picked from this corridor's
// own results (see buildStations): stations pass from z 512 to 632 and a crest fails them
// from 636 to 772, so the sweep starts on passing road, runs green, and meets the crest
// just as the last step comes on screen. A story with other steps passes its own numbers.
const STORY_DEFAULTS = {
  from: 486, // analysis-front z at progress 0
  travel: 200, // metres covered by progress 1
  analyseAt: 0.25, // progress at which the analysis starts sweeping
  classifyAt: null, // progress at which the cloud is shown classified (none by default)
};
// The scan sweeps in on its own clock the first time a third of the scene is on screen, a
// deliberate ~1.3 s load. It used to draw in with the scroll, which left the story's first
// screen nearly empty and read as a rendering bug.
const STORY_REVEAL_SPEED = 420; // m/s
const STORY_REVEAL_AT = 0.35; // share of the scene on screen that starts the sweep
const REVEAL_FAR = 560; // metres: the farthest points the scene ever draws
const STORY_CLASSIFY_SPEED = 700; // m/s the classification colours sweep out at
const STORY_EASE = 0.14; // per-frame easing of the camera toward the scroll position

const wrap = (d) => ((d % LOOP) + LOOP) % LOOP;
const around = (z, c) => {
  let d = wrap(z - c);
  if (d > LOOP / 2) d -= LOOP;
  return d;
};

function centerX(z) {
  const a = (2 * Math.PI * z) / LOOP;
  return 30 * Math.sin(2 * a) + 12 * Math.sin(3 * a + 1.3);
}
function roadY(z) {
  const bump = (c, h, w) => {
    const d = around(z, c);
    return h * Math.exp(-(d * d) / (2 * w * w));
  };
  return bump(230, 8, 52) + bump(780, 6, 42) + bump(1030, 2.5, 70);
}
/** Ground height at lateral offset `off` from the centreline: shoulder, ditch, rising bank. */
function groundY(z, off) {
  const a = Math.abs(off);
  const base = roadY(z);
  if (a < 7.2) return base;
  if (a < 11) return base - (a - 7.2) * 0.34;
  return base - 1.3 + (a - 11) * 0.2 * (0.75 + 0.35 * Math.sin(z * 0.011 + off * 0.07));
}

function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildWorld() {
  const rand = rng(20050817);
  const trees = [];
  for (let i = 0; i < 150; i++) {
    const side = rand() < 0.5 ? -1 : 1;
    const near = rand() < 0.3;
    trees.push({
      z: rand() * LOOP,
      off: side * (near ? 9.5 + rand() * 6 : 15 + rand() * 45),
      h: 7 + rand() * 9,
      r: 1.8 + rand() * 1.8,
    });
  }
  const buckets = Array.from({ length: LOOP / 20 }, () => []);
  for (const t of trees) buckets[Math.floor(t.z / 20)].push(t);

  // x, y, z, intensity, kind (0 surface, 1 marking, 2 ground, 3 tree)
  const pts = [];
  const push = (x, y, z, i, kind) => pts.push(x, y, z, i, kind);
  for (let n = 0; n < 9000; n++) {
    const z = rand() * LOOP;
    const off = (rand() * 2 - 1) * 7.2;
    push(centerX(z) + off, roadY(z), z, 0.3 + rand() * 0.22, 0);
  }
  for (let z = 0; z < LOOP; z += 1.1) {
    for (const off of [-3.6, 3.6]) push(centerX(z) + off, roadY(z) + 0.01, z, 0.72, 1);
    if (z % 12 < 3.2) push(centerX(z), roadY(z) + 0.01, z, 0.8, 1);
  }
  for (let n = 0; n < 9000; n++) {
    const z = rand() * LOOP;
    const side = rand() < 0.5 ? -1 : 1;
    const off = side * (7.4 + Math.pow(rand(), 0.8) * 60);
    push(centerX(z) + off, groundY(z, off), z, 0.14 + rand() * 0.24, 2);
  }
  for (const t of trees) {
    const baseY = groundY(t.z, t.off);
    for (let k = 0; k < 26; k++) {
      const hh = 0.35 + rand() * 0.65;
      const rr = t.r * (1 - hh * 0.55) * Math.sqrt(rand());
      const ang = rand() * Math.PI * 2;
      push(
        centerX(t.z) + t.off + Math.cos(ang) * rr,
        baseY + t.h * hh,
        t.z + Math.sin(ang) * rr,
        0.3 + rand() * 0.35,
        3
      );
    }
  }
  return { pts: new Float32Array(pts), trees, buckets };
}

function sightCheck(world, zv, d) {
  const ex = centerX(zv) + LANE_OFF;
  const ey = roadY(zv) + EYE_H;
  const zt = zv + d;
  const tx = centerX(zt) + LANE_OFF;
  const ty = roadY(zt) + OBJ_H;
  const steps = Math.max(10, Math.ceil(d / 5));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const z = zv + d * t;
    const x = ex + (tx - ex) * t;
    const y = ey + (ty - ey) * t;
    const off = x - centerX(z);
    if (groundY(z, off) > y) return Math.abs(off) < 7.2 ? "Vertical curvature" : "Terrain";
  }
  const first = Math.floor(wrap(zv) / 20);
  const count = Math.ceil(d / 20) + 1;
  const dx = tx - ex;
  const dz = d;
  for (let b = 0; b < count; b++) {
    for (const tr of world.buckets[(first + b) % world.buckets.length]) {
      const trz = zv + around(tr.z, zv);
      const trx = centerX(tr.z) + tr.off;
      const t = Math.max(0, Math.min(1, ((trx - ex) * dx + (trz - zv) * dz) / (dx * dx + dz * dz)));
      const px = ex + dx * t;
      const pz = zv + dz * t;
      if (
        Math.hypot(trx - px, trz - pz) < tr.r &&
        groundY(tr.z, tr.off) + tr.h > ey + (ty - ey) * t
      ) {
        return "Vegetation";
      }
    }
  }
  return null;
}

function buildStations(world) {
  const n = LOOP / STATION_M;
  const available = new Float32Array(n);
  const cause = new Array(n);
  for (let s = 0; s < n; s++) {
    const zv = s * STATION_M;
    let reach = MAX_LOOK;
    let why = null;
    for (let d = LOOK_STEP; d <= MAX_LOOK; d += LOOK_STEP) {
      const blocked = sightCheck(world, zv, d);
      if (blocked) {
        reach = d - LOOK_STEP;
        why = blocked;
        break;
      }
    }
    available[s] = reach;
    cause[s] = why;
  }
  return { available, cause };
}

const stationIndex = (z) => Math.floor(wrap(z) / STATION_M) % (LOOP / STATION_M);

function palette(r, g, b) {
  const out = [];
  for (let i = 0; i < 12; i++) {
    const row = [];
    const k = 0.25 + (0.75 * i) / 11;
    for (let a = 0; a < 8; a++) {
      row.push(
        `rgba(${Math.round(r * k)},${Math.round(g * k)},${Math.round(b * k)},${((a + 1) / 8).toFixed(3)})`
      );
    }
    out.push(row);
  }
  return out;
}
const SCAN = palette(196, 212, 228);
const PASS = palette(70, 214, 150);
const FAIL = palette(255, 140, 20);
// A classified cloud, coloured the way LiDAR tools show one (a story's "prepare" step).
// Exported so a story's legend uses exactly the colours drawn.
export const CLASS_COLORS = {
  road: [110, 160, 235],
  ground: [196, 164, 112],
  vegetation: [96, 196, 124],
};
// By point kind: surface, marking (stays white), ground, tree.
const CLASS_PAL = [
  palette(...CLASS_COLORS.road),
  SCAN,
  palette(...CLASS_COLORS.ground),
  palette(...CLASS_COLORS.vegetation),
];

/**
 * `progressRef` (a ref holding 0-1) switches to story mode: the page's scroll position,
 * not the clock, drives the scene: the scan draws in, optionally shows classified (from
 * `classifyAt`), then the analysis sweeps the road (from `analyseAt`) and the front meets a
 * failing crest. `from` and `travel` set where the front runs; `final` lights the readout.
 */
export default function ScanScene({
  className = "",
  progressRef = null,
  from = STORY_DEFAULTS.from,
  travel = STORY_DEFAULTS.travel,
  analyseAt = STORY_DEFAULTS.analyseAt,
  classifyAt = STORY_DEFAULTS.classifyAt,
  final = false,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const stationRef = useRef(null);
  const availableRef = useRef(null);
  const statusRef = useRef(null);
  const causeRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = wrapRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const world = buildWorld();
    const stations = buildStations(world);
    const story = Boolean(progressRef);
    // Where the colored sweep begins: the front's position when the analysis switches on.
    const sweepFrom = from + travel * analyseAt;
    const reduced =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      document.body.classList.contains("reduced-motion");

    let w = 0;
    let h = 0;
    let dpr = 1;
    const resize = () => {
      const rect = host.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      if (reduced) draw(performance.now());
    };

    // Start where the analysis front is about to meet the first crest, so the
    // opening seconds show a failing sight line. A story starts at its own mark.
    let camZ = story ? wrap(from - VEHICLE_AHEAD) : wrap(230 - VEHICLE_AHEAD - 120);
    let last = performance.now();
    const start = last;
    let lastHud = 0;
    // Story mode: when the scan's sweep began (see STORY_REVEAL_AT), and when the
    // classification sweep began; null until then.
    let revealFrom = null;
    let classifyFrom = null;

    // The readout before the analysis starts, by what the scene is doing.
    const WAITING = {
      reading: "Reading the scan",
      ready: "Scan ready",
      classifying: "Classifying the scan",
      classified: "Road surface found",
    };
    const setHud = (vehZ, mode) => {
      if (mode !== "analysing") {
        for (const ref of [stationRef, availableRef, causeRef]) {
          if (ref.current) ref.current.textContent = "—";
        }
        if (statusRef.current) {
          statusRef.current.textContent = WAITING[mode];
          statusRef.current.dataset.ok = "wait";
        }
        return;
      }
      const s = stationIndex(vehZ);
      const reachFt = stations.available[s] * FT;
      const ok = reachFt >= REQUIRED_FT;
      const ft = Math.round(vehZ * FT) + STATION_ORIGIN_FT;
      if (stationRef.current) {
        stationRef.current.textContent = `${Math.floor(ft / 100)}+${String(ft % 100).padStart(2, "0")}`;
      }
      if (availableRef.current) {
        availableRef.current.textContent =
          stations.available[s] >= MAX_LOOK
            ? `${Math.round(reachFt)}+ ft`
            : `${Math.round(reachFt)} ft`;
      }
      if (statusRef.current) {
        statusRef.current.textContent = ok ? "Meets AASHTO" : "Deficient";
        statusRef.current.dataset.ok = ok ? "true" : "false";
      }
      if (causeRef.current) causeRef.current.textContent = ok ? "—" : stations.cause[s] || "—";
    };

    function draw(now) {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      // Story progress: reduced motion shows the story's ending, the failing crest, at rest.
      const p = story ? (reduced ? 1 : Math.min(1, Math.max(0, progressRef.current || 0))) : 1;
      if (story) {
        const target = from + travel * p - VEHICLE_AHEAD;
        camZ = reduced ? wrap(target) : wrap(camZ + around(target, camZ) * STORY_EASE);
      } else if (!reduced) {
        camZ = wrap(camZ + SPEED * dt);
      }
      const vehZ = wrap(camZ + VEHICLE_AHEAD);
      const analysing = !story || p >= analyseAt;
      const classifying = story && classifyAt !== null && p >= classifyAt && !analysing;
      if (classifying && classifyFrom === null) classifyFrom = now;
      const classReach = classifying ? ((now - classifyFrom) / 1000) * STORY_CLASSIFY_SPEED : 0;
      const reveal = reduced
        ? Infinity
        : story
          ? revealFrom === null
            ? 0
            : ((now - revealFrom) / 1000) * STORY_REVEAL_SPEED
          : ((now - start) / 1000) * 520;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const wide = w >= 768;
      // A story on a phone keeps its readout top-right, so the road converges left of it.
      const cx = w * (wide ? 0.66 : story ? 0.34 : 0.5);
      const cy = h * (story && !wide ? 0.36 : 0.4);
      const focal = Math.max(h, w * 0.55) * 0.95;

      // Camera: a low drone shot behind the analysis front, looking down the corridor.
      const px = centerX(camZ) * 0.9;
      const py = roadY(camZ) + 15;
      const lookZ = camZ + 115;
      let fx = centerX(lookZ) - px;
      let fy = roadY(lookZ) - 1 - py;
      let fz = lookZ - camZ;
      const fl = Math.hypot(fx, fy, fz);
      fx /= fl;
      fy /= fl;
      fz /= fl;
      let rx = fz;
      let rz = -fx;
      const rl = Math.hypot(rx, rz);
      rx /= rl;
      rz /= rl;
      const ux = fy * rz;
      const uy = fz * rx - fx * rz;
      const uz = -fy * rx;

      const project = (x, y, zWorld) => {
        const vx = x - px;
        const vy = y - py;
        const vz = zWorld - camZ;
        const zc = vx * fx + vy * fy + vz * fz;
        if (zc < 2) return null;
        return {
          x: cx + ((vx * rx + vz * rz) / zc) * focal,
          y: cy - ((vx * ux + vy * uy + vz * uz) / zc) * focal,
          zc,
        };
      };

      const pts = world.pts;
      for (let i = 0; i < pts.length; i += 5) {
        const dzCam = around(pts[i + 2], camZ);
        if (dzCam < 4 || dzCam > REVEAL_FAR) continue;
        const zWorld = camZ + dzCam;
        const x = pts[i];
        const y = pts[i + 1];
        const vx = x - px;
        const vy = y - py;
        const vz = zWorld - camZ;
        const zc = vx * fx + vy * fy + vz * fz;
        if (zc < 4 || zc > reveal) continue;
        const sx = cx + ((vx * rx + vz * rz) / zc) * focal;
        if (sx < -4 || sx > w + 4) continue;
        const sy = cy - ((vx * ux + vy * uy + vz * uz) / zc) * focal;
        if (sy < -4 || sy > h + 4) continue;

        const kind = pts[i + 4];
        const inten = pts[i + 3];
        let fade = Math.min(1, zc / 30) * Math.min(1, Math.max(0, (REVEAL_FAR - zc) / 190));
        let pal = SCAN;
        let boost = 1;
        const toFront = around(pts[i + 2], vehZ);
        const analysed =
          analysing &&
          kind <= 1 &&
          toFront < 0 &&
          dzCam > 0 &&
          (!story || around(pts[i + 2], sweepFrom) >= 0);
        if (analysed) {
          const ok = stations.available[stationIndex(pts[i + 2])] * FT >= REQUIRED_FT;
          pal = ok ? PASS : FAIL;
          boost = 1.2;
        } else if (analysing && Math.abs(toFront) < 1.6) {
          // The analysis front itself: a bright scan line sweeping the corridor.
          boost = 2.4;
          fade = Math.min(1, fade * 1.6);
        } else if (classifying && zc < classReach) {
          pal = CLASS_PAL[kind];
          // Brighter where the colours are arriving, so the classification reads as a pass.
          if (classReach - zc < 14) boost = 1.8;
        }
        const front = reveal - zc;
        if (front < 26) fade = Math.min(1, fade + (1 - front / 26) * 0.6);
        const level = Math.min(11, Math.round(inten * boost * 11));
        const alpha = Math.max(0, Math.min(7, Math.round(fade * 7)));
        if (alpha === 0 && fade < 0.07) continue;
        const size = Math.max(0.9, Math.min(kind === 1 ? 3.4 : 2.8, (kind === 1 ? 640 : 520) / zc));
        ctx.fillStyle = pal[level][alpha];
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
      }

      // The live sight line from the analysis front.
      const s = stationIndex(vehZ);
      const reach = Math.min(stations.available[s], REQUIRED_M);
      const ok = stations.available[s] >= REQUIRED_M;
      const eye = project(centerX(vehZ) + LANE_OFF, roadY(vehZ) + EYE_H, vehZ);
      const hit = project(
        centerX(vehZ + reach) + LANE_OFF,
        roadY(vehZ + reach) + OBJ_H,
        vehZ + reach
      );
      const target = project(
        centerX(vehZ + REQUIRED_M) + LANE_OFF,
        roadY(vehZ + REQUIRED_M) + OBJ_H,
        vehZ + REQUIRED_M
      );
      const lineDelay = story || reduced ? 0 : 0.9;
      if (analysing && eye && hit && target && (now - start) / 1000 > lineDelay) {
        const color = ok ? "70,214,150" : "255,140,20";
        ctx.lineCap = "round";
        ctx.lineWidth = 2;
        ctx.strokeStyle = `rgba(${color},0.95)`;
        ctx.shadowColor = `rgba(${color},0.8)`;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.moveTo(eye.x, eye.y);
        ctx.lineTo(hit.x, hit.y);
        ctx.stroke();
        ctx.shadowBlur = 0;
        if (!ok) {
          ctx.setLineDash([4, 5]);
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = "rgba(255,140,20,0.45)";
          ctx.beginPath();
          ctx.moveTo(hit.x, hit.y);
          ctx.lineTo(target.x, target.y);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.fillStyle = `rgba(${color},1)`;
        ctx.beginPath();
        ctx.arc(eye.x, eye.y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = `rgba(${color},0.35)`;
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.arc(eye.x, eye.y, 9, 0, Math.PI * 2);
        ctx.stroke();
        const d = 5;
        ctx.fillStyle = ok ? "rgba(70,214,150,1)" : "rgba(255,140,20,0.9)";
        ctx.beginPath();
        ctx.moveTo(target.x, target.y - d);
        ctx.lineTo(target.x + d, target.y);
        ctx.lineTo(target.x, target.y + d);
        ctx.lineTo(target.x - d, target.y);
        ctx.closePath();
        ctx.fill();
      }

      if (now - lastHud > 160) {
        lastHud = now;
        const mode = analysing
          ? "analysing"
          : classifying
            ? classReach >= REVEAL_FAR
              ? "classified"
              : "classifying"
            : reveal >= REVEAL_FAR
              ? "ready"
              : "reading";
        setHud(vehZ, mode);
      }
    }

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(host);

    if (reduced) {
      draw(performance.now());
      return () => ro.disconnect();
    }

    let raf = 0;
    let running = false;
    const loop = (now) => {
      draw(now);
      raf = requestAnimationFrame(loop);
    };
    const play = () => {
      if (running) return;
      running = true;
      last = performance.now();
      raf = requestAnimationFrame(loop);
    };
    const pause = () => {
      running = false;
      cancelAnimationFrame(raf);
    };
    let onScreen = true;
    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        if (story && revealFrom === null && entry.intersectionRatio >= STORY_REVEAL_AT) {
          revealFrom = performance.now();
        }
        if (onScreen && !document.hidden) play();
        else pause();
      },
      { threshold: story ? [0, STORY_REVEAL_AT] : 0 }
    );
    io.observe(host);
    const onVisibility = () => (document.hidden || !onScreen ? pause() : play());
    document.addEventListener("visibilitychange", onVisibility);
    play();

    return () => {
      pause();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [progressRef, from, travel, analyseAt, classifyAt]);

  return (
    <div
      ref={wrapRef}
      className={`${styles.scene} ${progressRef ? styles.story : ""} ${className}`.trim()}
      data-final={final ? "" : undefined}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className={styles.canvas} />
      <div className={styles.hud}>
        <p className={styles.hudHead}>
          <span className={styles.live} />
          Stopping sight distance
          <span className={styles.hudMeta}>55 mph</span>
        </p>
        <dl className={styles.readout}>
          <div>
            <dt>Station</dt>
            <dd ref={stationRef}>—</dd>
          </div>
          <div>
            <dt>Available</dt>
            <dd ref={availableRef}>—</dd>
          </div>
          <div>
            <dt>Required</dt>
            <dd>{REQUIRED_FT} ft</dd>
          </div>
          <div>
            <dt>Blocked by</dt>
            <dd ref={causeRef}>—</dd>
          </div>
        </dl>
        <p className={styles.status} ref={statusRef} data-ok="true">
          Meets AASHTO
        </p>
      </div>
    </div>
  );
}
